import React from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useSite } from '../context/SiteContext';
import { FrontEndRoutes } from '../config/front-end-routes';
import { trackContactEvent } from '../utils/trackingEvents';
import Location from './Location';
import ContactForm from './ContactForm';

const Home: React.FC = () => {
  const { config, serviceGroups, getPage, whatsappUrl, imageUrl } = useSite();
  
  // Tentar obter página INDEX do contexto, senão usar fallback
  const indexPage = getPage('index');
  const page = indexPage || getPage('home');

  const heroStyle: React.CSSProperties = { minHeight: '60vh' };

  // Seção "Por que nos escolher" — usa page sections ou fallback hardcoded
  const whySection = page?.sections?.find(s => s.section_key === 'por-que-nos-escolher' || s.section_key === 'diferenciais');
  const defaultWhyItems = [
    { icon: 'fas fa-check', title: 'Equipe Altamente Qualificada', text: 'Profissionais especializados e em constante atualização.' },
    { icon: 'fas fa-check', title: 'Tecnologia de Ponta', text: 'Equipamentos modernos para diagnósticos precisos.' },
    { icon: 'fas fa-check', title: 'Atendimento Personalizado', text: 'Plano de tratamento individualizado para cada paciente.' },
  ];

  // Renderizar seções dinâmicas da página INDEX
  const renderDynamicSections = () => {
    if (!page?.sections) return null;

    return page.sections
      .filter(s => s.section_key !== 'por-que-nos-escolher' && s.section_key !== 'diferenciais')
      .sort((a, b) => a.order - b.order)
      .map((section, idx) => (
        <section key={section.id} className={`py-5 ${idx % 2 === 0 ? 'bg-profile-5' : 'bg-white'}`}>
          <Container>
            {(section.section_title || section.section_subtitle) && (
              <Row className="text-center mb-4">
                <Col>
                  {section.section_title && <h2 className="text-profile-1 fw-bold mb-3">{section.section_title}</h2>}
                  {section.section_subtitle && <p className="lead text-muted">{section.section_subtitle}</p>}
                </Col>
              </Row>
            )}
            {section.section_content && (
              <Row className="mb-4">
                <Col lg={section.section_image ? 6 : 12}>
                  <p className="text-profile-4">{section.section_content}</p>
                </Col>
                {section.section_image && (
                  <Col lg={6}>
                    <img src={imageUrl(section.section_image)} alt={section.section_title || ''} className="img-fluid rounded shadow" />
                  </Col>
                )}
              </Row>
            )}
            {section.items && section.items.length > 0 && (
              <Row className="g-4">
                {section.items.map(item => (
                  <Col md={6} lg={4} key={item.id}>
                    <Card className="h-100 shadow-sm border-0 hover-card">
                      <Card.Body className="p-4 text-center">
                        {item.item_icon && (
                          <div className="bg-profile-3 bg-opacity-25 rounded-circle d-flex align-items-center justify-content-center mb-3 mx-auto" style={{ width: '60px', height: '60px' }}>
                            <i className={`${item.item_icon} fs-3 text-profile-3`}></i>
                          </div>
                        )}
                        {item.item_title && <Card.Title className="text-profile-1 fw-bold">{item.item_title}</Card.Title>}
                        {item.item_description && <Card.Text className="text-muted">{item.item_description}</Card.Text>}
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </Container>
        </section>
      ));
  };

  return (
    <>
      {/* Hero */}
      <section className="bg-profile-1 text-white py-5" style={heroStyle}>
        <Container className="d-flex align-items-center h-100">
          <Row className="w-100">
            <Col lg={8} className="mx-auto text-center">
              <h1 className="display-4 fw-bold mb-4">{config?.hero_title || config?.site_title}</h1>
              <p className="lead mb-4">{config?.hero_subtitle || config?.site_description}</p>

              {serviceGroups.length > 0 && (
                <div className="d-flex justify-content-center flex-wrap gap-2 mb-4">
                  {serviceGroups.filter(g => g.highlight).slice(0, 4).map(g => (
                    <span key={g.id} className="badge bg-profile-3 text-profile-1 p-2 fs-6">{g.description}</span>
                  ))}
                </div>
              )}

              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link to={FrontEndRoutes.SERVICES} className="text-decoration-none">
                  <Button variant="warning" size="lg" className="text-profile-1 fw-bold">
                    <i className="fas fa-clipboard-list me-2"></i>Nossos Serviços
                  </Button>
                </Link>
                <Button
                  variant="light" size="lg" className="text-profile-1 fw-bold"
                  href={whatsappUrl} target="_blank" rel="noopener noreferrer"
                  onClick={trackContactEvent}
                >
                  <i className="fab fa-whatsapp me-2"></i>Agendar via WhatsApp
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Serviços resumo */}
      {config?.show_services !== false && serviceGroups.length > 0 && (
        <section className="py-5 bg-profile-5">
          <Container>
            <Row className="text-center mb-5">
              <Col>
                <h2 className="display-5 fw-bold text-profile-1 mb-3">{config?.services_header_title || 'Nossos Serviços'}</h2>
                <div className="bg-profile-3" style={{ width: '80px', height: '4px', margin: '0 auto' }}></div>
                <p className="mt-4 text-muted">{config?.services_header_subtitle || ''}</p>
              </Col>
            </Row>
            <Row className="g-4">
              {serviceGroups.slice(0, 3).map(group => (
                <Col md={4} key={group.id}>
                  <Card className="h-100 shadow-sm border-0 hover-card">
                    <Card.Body className="p-4">
                      <div className="bg-profile-3 bg-opacity-25 rounded-circle d-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                        <i className={`${group.icon_class || 'fas fa-tooth'} fs-3 text-profile-3`}></i>
                      </div>
                      <Card.Title className="text-profile-1 fw-bold">{group.description}</Card.Title>
                      <Card.Text className="text-muted">{group.detailed_description || `Tratamento especializado em ${group.description.toLowerCase()}.`}</Card.Text>
                      <Button variant="link" className="text-profile-1 p-0 fw-semibold" href={whatsappUrl} target="_blank" rel="noopener noreferrer" onClick={trackContactEvent}>
                        Agendar Consulta <i className="fab fa-whatsapp ms-1"></i>
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Container>
        </section>
      )}

      {/* Por que nos escolher */}
      <section className="py-5 bg-profile-1 text-white">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              {whySection?.section_image ? (
                <img src={imageUrl(whySection.section_image)} alt="Consultório" className="img-fluid rounded shadow" />
              ) : config?.hero_image ? (
                <img src={imageUrl(config.hero_image)} alt="Consultório" className="img-fluid rounded shadow" />
              ) : (
                <div className="bg-profile-3 bg-opacity-10 rounded d-flex align-items-center justify-content-center" style={{ height: '400px' }}>
                  <i className="fas fa-clinic-medical fs-1 text-profile-3"></i>
                </div>
              )}
            </Col>
            <Col lg={6}>
              <h2 className="display-5 fw-bold mb-4">{whySection?.section_title || 'Por que escolher nossa clínica?'}</h2>
              <div className="bg-profile-3 mb-4" style={{ width: '80px', height: '4px' }}></div>

              {whySection?.items && whySection.items.length > 0 ? (
                whySection.items.map(item => (
                  <div className="mb-4" key={item.id}>
                    <div className="d-flex align-items-start">
                      <div className="bg-profile-3 bg-opacity-25 rounded-circle p-3 me-3">
                        <i className={`${item.item_icon || 'fas fa-check'} text-profile-3`}></i>
                      </div>
                      <div>
                        {item.item_title && <h5 className="fw-bold">{item.item_title}</h5>}
                        {item.item_description && <p className="text-white-50">{item.item_description}</p>}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                defaultWhyItems.map((item, i) => (
                  <div className="mb-4" key={i}>
                    <div className="d-flex align-items-start">
                      <div className="bg-profile-3 bg-opacity-25 rounded-circle p-3 me-3">
                        <i className={`${item.icon} text-profile-3`}></i>
                      </div>
                      <div>
                        <h5 className="fw-bold">{item.title}</h5>
                        <p className="text-white-50">{item.text}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </Col>
          </Row>
        </Container>
      </section>

      {/* Seções dinâmicas da página INDEX */}
      {renderDynamicSections()}

      {/* Agendamento */}
      <section className="py-5 bg-profile-5">
        <Container>
          <Row className="justify-content-center">
            <Col lg={10}>
              <Card className="shadow border-0 overflow-hidden">
                <Row className="g-0">
                  <Col md={5} className="bg-profile-1 text-white p-4 p-md-5">
                    <h3 className="fw-bold mb-4">Agende sua consulta</h3>
                    <p className="mb-4">Clique no botão do WhatsApp e fale diretamente com nossa equipe para agendar seu horário.</p>
                    {config?.phone && (
                      <div className="d-flex align-items-center mb-3">
                        <div className="bg-profile-3 bg-opacity-25 rounded-circle p-3 me-3"><i className="fas fa-phone text-profile-3"></i></div>
                        <div><h6 className="fw-bold mb-0">Telefone</h6><small>{config.phone}</small></div>
                      </div>
                    )}
                    {config?.business_hours && config.business_hours.length > 0 && (
                      <div className="d-flex align-items-center mb-3">
                        <div className="bg-profile-3 bg-opacity-25 rounded-circle p-3 me-3"><i className="fas fa-clock text-profile-3"></i></div>
                        <div>
                          <h6 className="fw-bold mb-0">Horário</h6>
                          {config.business_hours.map((h, i) => <small key={i} className="d-block">{h.day}: {h.hours}</small>)}
                        </div>
                      </div>
                    )}
                    {config?.address && (
                      <div className="d-flex align-items-center">
                        <div className="bg-profile-3 bg-opacity-25 rounded-circle p-3 me-3"><i className="fas fa-map-marker-alt text-profile-3"></i></div>
                        <div><h6 className="fw-bold mb-0">Endereço</h6><small>{config.address}</small></div>
                      </div>
                    )}
                  </Col>
                  <Col md={7} className="p-4 p-md-5">
                    <ContactForm />
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      <Location />

      <style>{`
        .hover-card { transition: transform 0.3s ease, box-shadow 0.3s ease; }
        .hover-card:hover { transform: translateY(-5px); box-shadow: 0 10px 30px rgba(20,63,63,0.2); }
      `}</style>
    </>
  );
};

export default Home;
